/**
 * AEGISPanel.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * Right-anchored AEGIS governance slide-in panel.
 * This is the AEGIS glass-box governance surface — every AEGIS decision
 * is citable here. NOT a chatbot. A governance panel.
 *
 * Per ELYON-AEGIS-PATTERN-LIBRARY-v1 §1-§4:
 * - Citation Chip pattern (P1) for provenance
 * - Confidence Indicator (P2) for governance
 * - Decision Twin (P4) for attestation
 * - "Why?" affordance per pattern library
 *
 * Phase 1: Thread renders in stub mode. AEGIS backend wired in follow-on session.
 * §4.NEW-1 gate: panel opens/closes without error. Thread renders. ✓
 *
 * Opens via: TopBar AEGIS button, keyboard shortcut ⌘J.
 * Closes via: X button, Escape, overlay click.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, ChevronRight, Send } from 'lucide-react';
import { useMotionPreset } from '../motion/presets';
import { useAegisPanelOpen, useAegisDraftQuery, useUserPrefsStore } from '../stores/userPrefsStore.js';
import { useAegisAccess, useTenantId } from '../stores/tenantStore.js';
import governedAegisCall from '../services/aegis/governedAegisCall.js';
import { mapResultToMessage, isRealAnswer } from './aegisMessageMapping.js';
import { aegisLocalSeedAnswer } from './aegisLocalSeedAnswer.js';

/**
 * Confidence indicator per ELYON-AEGIS-PATTERN-LIBRARY-v1 §4.
 * Three-tier visual: high / medium / low.
 */
function ConfidenceIndicator({ confidence }) {
  const tier = confidence >= 0.85 ? 'high' : confidence >= 0.7 ? 'medium' : 'low';

  if (tier === 'high') {
    return (
      <span className="aegis-confidence-high" aria-label="High confidence">
        <span className="aegis-confidence-dot" aria-hidden="true" />
        <span className="aegis-confidence-label">AEGIS</span>
      </span>
    );
  }

  if (tier === 'medium') {
    return (
      <span className="aegis-confidence-medium" aria-label={`Confidence: ${confidence.toFixed(2)}`}>
        <span className="aegis-confidence-dot" aria-hidden="true" />
        <span className="aegis-confidence-label">AEGIS · {confidence.toFixed(2)}</span>
      </span>
    );
  }

  // Low confidence — banner with force-acknowledge
  return (
    <div className="elyon-aegis-confidence-low" role="alert" aria-live="polite">
      ⚠ Low confidence ({confidence.toFixed(2)}) — review carefully
    </div>
  );
}

/**
 * Stub thread message for Phase 1 governance panel.
 */
/**
 * Renders an AEGIS assistant message. Attestation chrome (Confidence Indicator
 * P2, "Why?" Decision Twin P4) appears ONLY when there is real attestation —
 * never a hardcoded placeholder. Message kinds: 'notice' (info banner, no
 * attestation), 'answer' (graded, attested), 'unavailable' (honest non-answer),
 * plus a pending state during the call.
 */
function AegisMessage({ message }) {
  const [whyOpen, setWhyOpen] = useState(false);

  if (message.pending) {
    return (
      <div className="aegis-message-pending" role="status" aria-live="polite">
        AEGIS is reviewing…
      </div>
    );
  }

  const hasConfidence = typeof message.confidence === 'number'
    && message.confidence >= 0 && message.confidence <= 1;
  const isNotice = message.kind === 'notice';
  // Show the Decision Twin only when there is something real to attest:
  // a graded answer, or any message carrying a real decisionId (auditable refusal).
  const showAttestation = !isNotice && (message.kind === 'answer' || Boolean(message.decisionId));

  return (
    <div className="elyon-aegis-message-card">
      {/* Confidence indicator (Pattern 2) — only for a real graded answer */}
      {hasConfidence && <ConfidenceIndicator confidence={message.confidence} />}

      <p className="aegis-message-body">{message.content}</p>

      {showAttestation && (
        <>
          {/* "Why?" affordance (Pattern 4 — Decision Twin attestation) */}
          <button
            className="elyon-aegis-why-btn"
            onClick={() => setWhyOpen((v) => !v)}
            aria-expanded={whyOpen}
          >
            <ChevronRight
              size={12}
              strokeWidth={1.5}
              aria-hidden="true"
              className={whyOpen ? 'aegis-chevron--open' : 'aegis-chevron--closed'}
            />
            Why?
          </button>

          {whyOpen && (
            <div className="aegis-decision-twin" role="region" aria-label="Decision Twin attestation">
              <div className="aegis-decision-twin-title">
                Decision Twin · {message.decisionId ?? 'No attestation'}
              </div>
              <div className="aegis-decision-twin-rows">
                <div><strong>Sources:</strong> {message.sources?.length ? message.sources.join(', ') : 'No sources cited'}</div>
                <div><strong>Confidence:</strong> {hasConfidence ? message.confidence.toFixed(2) : '—'}</div>
                <div><strong>Model:</strong> {message.model ?? 'unavailable'}</div>
              </div>
              <div className="aegis-decision-twin-kaudit">
                KAUDIT-7 — immutable attestation chain
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Raw Motion variants removed — useMotionPreset() called inside component.

export function AEGISPanel() {
  const isOpen   = useAegisPanelOpen();
  const draftQuery = useAegisDraftQuery();
  const { setAegisPanelOpen, setAegisDraftQuery } = useUserPrefsStore();
  const aegisAccess = useAegisAccess();
  const tenantId = useTenantId();

  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages]     = useState([
    {
      // Info notice — NOT an attested answer. Carries no confidence/decisionId/
      // model, so the renderer shows no Confidence Indicator or Decision Twin.
      id: 'notice-1',
      role: 'assistant',
      kind: 'notice',
      content: 'AEGIS governance panel. Ask an operations question — responses are governed and carry a Decision Twin attestation (model, sources, confidence) when AEGIS answers.',
    },
  ]);
  const closeRef = useRef(null);
  const inputRef = useRef(null);

  // Motion presets — BINDING: no raw Framer Motion props in component code.
  const overlayMotion = useMotionPreset('appear');   // overlay fade
  const panelMotion   = useMotionPreset('slide');    // DIFF-001: slide-in per CFI-FLOOR-AEGIS-001
  const sendTap       = useMotionPreset('micro');    // button whileTap

  // ⌘J keyboard shortcut (CFI-FLOOR-AEGIS-001)
  useEffect(() => {
    function onKeyDown(e) {
      const isMac = navigator.platform.startsWith('Mac');
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (modKey && e.key === 'j') {
        e.preventDefault();
        setAegisPanelOpen(!isOpen);
      }
      if (e.key === 'Escape' && isOpen) {
        setAegisPanelOpen(false);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setAegisPanelOpen]);

  // Focus close button when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => closeRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => setAegisPanelOpen(false), [setAegisPanelOpen]);

  // ELYON-AEGIS-PANEL-WIRE-1: real governed AEGIS call. Appends the user turn +
  // a pending placeholder, awaits governedAegisCall (the ONLY sanctioned path —
  // no direct SDK), then replaces the placeholder in-place with a mapped message.
  // mapResultToMessage gates all attestation on real result fields; on a mocked
  // local stub / failed CF / governance block it renders an HONEST unavailable
  // state — never a fabricated answer or attestation.
  const handleSend = useCallback(async (explicitQ) => {
    // explicitQ is a string only when called programmatically (draft auto-submit);
    // the onClick handler passes a DOM event, which we ignore in favor of the input.
    const q = (typeof explicitQ === 'string' ? explicitQ : inputValue).trim();
    if (!q || sending) return;
    const pendingId = `aegis-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { id: `user-${Date.now()}`, role: 'user', content: q },
      { id: pendingId, role: 'assistant', pending: true, content: '' },
    ]);
    setInputValue('');
    setSending(true);

    if (!tenantId) {
      setMessages((m) => m.map((msg) => msg.id === pendingId
        ? { id: pendingId, role: 'assistant', kind: 'unavailable',
            content: 'AEGIS unavailable — no active tenant context.',
            confidence: null, decisionId: null, sources: [], model: null }
        : msg));
      setSending(false);
      return;
    }

    try {
      const result = await governedAegisCall({
        agentId: 'AEGIS-COMMAND-CENTER',
        module: 'command-center',
        decisionType: 'operator_query',
        tenantId,
        prompt: q,
        context: { surface: 'command-center', source: 'aegis-panel' },
      });
      if (isRealAnswer(result)) {
        const mapped = mapResultToMessage(result, pendingId);
        setMessages((m) => m.map((msg) => (msg.id === pendingId ? mapped : msg)));
      } else {
        // Governed model not available in this environment. Rather than a dead
        // "(local stub)" non-answer, ground a LOCAL DATA SUMMARY in the operator's
        // seeded records (clearly labeled, real source ids, no fabricated
        // attestation — see aegisLocalSeedAnswer). Falls back to the honest
        // "unavailable" message when there's nothing to summarize.
        const seed = await aegisLocalSeedAnswer(q, tenantId);
        if (seed) {
          setMessages((m) => m.map((msg) => msg.id === pendingId
            ? { id: pendingId, role: 'assistant', kind: 'notice',
                content: seed.content, confidence: null, decisionId: null,
                sources: seed.sources ?? [], model: null }
            : msg));
        } else {
          const mapped = mapResultToMessage(result, pendingId);
          setMessages((m) => m.map((msg) => (msg.id === pendingId ? mapped : msg)));
        }
      }
    } catch (err) {
      setMessages((m) => m.map((msg) => msg.id === pendingId
        ? { id: pendingId, role: 'assistant', kind: 'unavailable',
            content: 'AEGIS backend error — please try again.',
            confidence: null, decisionId: null, sources: [], model: null,
            errorText: String(err?.message ?? err) }
        : msg));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [inputValue, sending, tenantId]);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  // When a surface opens the panel with a staged draft query (e.g. the Command
  // Center prompt, §4.6), AUTO-SUBMIT it — the operator already asked; don't make
  // them retype/re-send. Consume-once: clear the draft so it never re-fires.
  // (Declared after handleSend so it isn't referenced before initialization.)
  useEffect(() => {
    if (isOpen && draftQuery) {
      const q = draftQuery;
      setAegisDraftQuery('');
      handleSend(q);
    }
  }, [isOpen, draftQuery, setAegisDraftQuery, handleSend]);

  return (
    <AnimatePresence>
      {isOpen && (
        /* Overlay: full-screen flex container — CSS class owns all positioning */
        <motion.div
          className="elyon-aegis-overlay"
          {...overlayMotion}
          onClick={handleClose}
          aria-hidden="true"
        >
          {/* Panel: centered by overlay flex — CSS class owns dimensions */}
          <motion.div
            className="elyon-aegis-panel"
            {...panelMotion}
            role="dialog"
            aria-modal="true"
            aria-label="AEGIS governance panel"
            data-testid="aegis-panel"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="elyon-aegis-panel-header">
              <div className="elyon-aegis-panel-title">
                <span className="elyon-aegis-accent" aria-hidden="true" />
                AEGIS
              </div>

              {/* Access tier badge — CSS class, no inline style */}
              {aegisAccess !== 'none' && (
                <span className="aegis-tier-badge">{aegisAccess}</span>
              )}

              <button
                ref={closeRef}
                className="elyon-aegis-panel-close"
                onClick={handleClose}
                aria-label="Close AEGIS panel"
              >
                <X size={16} strokeWidth={1.5} aria-hidden="true" />
              </button>
            </div>

            {/* Thread body */}
            <div className="elyon-aegis-panel-body">
              <div className="elyon-aegis-thread">
                {/* Messages */}
                <div
                  className="elyon-aegis-messages"
                  role="log"
                  aria-label="AEGIS conversation"
                  aria-live="polite"
                >
                  {messages.map((msg) =>
                    msg.role === 'assistant' ? (
                      <AegisMessage key={msg.id} message={msg} />
                    ) : (
                      <div key={msg.id} className="aegis-user-message">
                        {msg.content}
                      </div>
                    )
                  )}
                </div>

                {/* Composer */}
                <div className="elyon-aegis-composer">
                  <div className="elyon-aegis-composer-row">
                    <textarea
                      ref={inputRef}
                      className="elyon-aegis-input"
                      placeholder="Ask AEGIS a governance question…"
                      aria-label="AEGIS input"
                      rows={1}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={handleInputKeyDown}
                    />
                    <motion.button
                      className="elyon-aegis-send-btn"
                      onClick={handleSend}
                      disabled={!inputValue.trim() || sending}
                      aria-label="Send to AEGIS"
                      whileTap={sendTap.whileTap}
                    >
                      <Send size={14} strokeWidth={1.5} aria-hidden="true" />
                    </motion.button>
                  </div>
                  <p className="elyon-aegis-composer-hint">
                    AEGIS responses carry full attestation chains per KAUDIT-7.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
